'use client'

import { forwardRef, useEffect, useState } from 'react'
import { TextInput, Image, Group, Button } from '@mantine/core'
import { useMentorSearchStore } from '@/stores/useMentorSearchStore'
import { IconSearch } from '@tabler/icons-react'
import { useMediaQuery } from '@mantine/hooks'
import { publicImageUrl } from '@/lib/staticAssets'

const rotatingExamples = [
  'I want to learn frontend development…',
  'Looking for a mentor in AI near me…',
  'Someone who knows growth marketing…',
  'I need help with UX design in NZ…',
  'Help me with software engineering best practices…',
  'Looking for a DevOps mentor for cloud deployment…',
  'Someone who knows information architecture…',
  'I want to break into data science…',
  'Need guidance on product management strategy…',
  'Looking for help with backend systems…',
]

interface MentorSearchInputProps {
  value: string
  onChange: (value: string) => void
  onSearch: () => void
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  showButton?: boolean
  maxLength?: number
  style?: React.CSSProperties
}

const MentorSearchInput = forwardRef<HTMLInputElement, MentorSearchInputProps>(
  ({ 
    value,
    onChange,
    onSearch,
    onKeyDown,
    showButton = true,
    maxLength = 100,
    style
  }, ref) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const placeholderIndex    = useMentorSearchStore((s) => s.placeholderIndex);
    const setPlaceholderIndex = useMentorSearchStore((s) => s.setPlaceholderIndex);
    const [isAnimating, setIsAnimating] = useState(false);

    useEffect(() => {
      let timeoutId: NodeJS.Timeout;

      const setNextExample = () => {
        setIsAnimating(true);
        // Wait for fade out
        setTimeout(() => {
          let newIdx;
          do {
            newIdx = Math.floor(Math.random() * rotatingExamples.length);
          } while (newIdx === placeholderIndex && rotatingExamples.length > 1);
          setPlaceholderIndex(newIdx);
          setIsAnimating(false);
        }, 150); // Half of transition duration
      };

      const scheduleNext = () => {
        const randomDelay = Math.random() * 2000 + 3000; // 3000-5000ms
        timeoutId = setTimeout(() => {
          setNextExample();
          scheduleNext(); // Schedule the next one
        }, randomDelay);
      };

      scheduleNext();

      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }, [placeholderIndex, setPlaceholderIndex]);

    const searchInput = (
      <div style={{ position: 'relative', flexGrow: 1 }}>
        <TextInput
          ref={ref}
          size="lg"
          radius="md"
          leftSection={
            <Image
              src={publicImageUrl('images/mentorsub-logo-standalone.svg')}
              alt="MentorSub logo"
              w={18}
              h={18}
            />
          }
          placeholder=""
          value={value}
          onKeyDown={onKeyDown}
          onChange={(e) => onChange(e.currentTarget.value)}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          aria-label="Search for mentors by expertise, industry, or location"
          styles={{
            input: {
              border: 'none',
              ...style
            }
          }}
          data-testid="input"
          maxLength={maxLength}
        />
        {!value && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              display: 'grid',
              placeItems: 'center start',
              paddingLeft: '52px',
              fontSize: '16px',
              fontStyle: 'italic',
              color: '#000',
              opacity: 0.5,
              lineHeight: 1,
            }}
          >
            <span
              style={{
                opacity: isAnimating ? 0 : 1,
                transform: isAnimating ? 'translateY(-4px)' : 'translateY(0)',
                transition: 'opacity 0.3s ease, transform 0.3s ease',
              }}
            >
              {rotatingExamples[placeholderIndex]}
            </span>
          </div>
        )}
      </div>
    )

    if (!showButton) {
      return searchInput
    }

    return (
      <Group 
        wrap="nowrap" 
        justify="center" 
        align="stretch" 
        w="100%" 
        maw={isMobile ? '100%' : 700} 
        mx="auto" 
        gap="xs"
        style={isMobile ? { paddingLeft: 0, paddingRight: 0 } : {}}
      >
        {searchInput}
        <Button 
          size="lg" 
          onClick={onSearch} 
          px={isMobile ? 'lg' : 'xl'}
          style={{ flexShrink: 0, height: style?.height || '60px' }}
        >
          {isMobile ? <IconSearch size={20} /> : 'Search'}
        </Button>
      </Group>
    )
  }
)

MentorSearchInput.displayName = 'MentorSearchInput'

export default MentorSearchInput